import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { RotateCcw } from "lucide-react";
import StyledDropdown from "src/components/reusable/StyledDropdown";
import {
  BRANCHES,
  CHANNEL_MIX,
  DATE_RANGES,
  DEMO_TODAY,
  ORDER_TYPE_MIX,
  RANGE_DAYS,
  type ChannelId,
  type DateRangeId,
  type OrderTypeId,
} from "../data/restaurantDemoData";
import { useRestaurantFormatters } from "../utils/restaurantFormatters";
import type { RestaurantFilters } from "../utils/restaurantMetrics";

interface RestaurantFilterBarProps {
  filters: RestaurantFilters;
  // eslint-disable-next-line no-unused-vars
  setFilter: <K extends keyof RestaurantFilters>(key: K, value: RestaurantFilters[K]) => void;
  isFiltered: boolean;
  onReset: () => void;
}

const DATE_RANGE_LABEL_KEYS: Record<DateRangeId, string> = {
  last_7_days: "restaurantAnalytics.filters.last7Days",
  last_30_days: "restaurantAnalytics.filters.last30Days",
  last_90_days: "restaurantAnalytics.filters.last90Days",
  ytd: "restaurantAnalytics.filters.ytd",
};

function isoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function presetStartDate(days: number): string {
  const date = new Date(DEMO_TODAY);
  date.setUTCDate(date.getUTCDate() - (days - 1));
  return isoDate(date);
}

function presetStartDateFor(id: DateRangeId): string {
  if (id === "ytd") {
    return isoDate(new Date(Date.UTC(DEMO_TODAY.getUTCFullYear(), 0, 1)));
  }
  return presetStartDate(RANGE_DAYS[id]);
}

const ORDER_TYPE_LABEL_KEYS: Record<OrderTypeId, string> = {
  dine_in: "restaurantAnalytics.orderTypes.dineIn",
  takeaway: "restaurantAnalytics.orderTypes.takeaway",
  delivery: "restaurantAnalytics.orderTypes.delivery",
  drive_thru: "restaurantAnalytics.orderTypes.driveThru",
  pickup: "restaurantAnalytics.orderTypes.pickup",
};

const CHANNEL_LABEL_KEYS: Record<ChannelId, string> = {
  mobile_app: "restaurantAnalytics.channels.mobileApp",
  website: "restaurantAnalytics.channels.website",
  kiosk: "restaurantAnalytics.channels.kiosk",
  pos: "restaurantAnalytics.channels.pos",
  call_center: "restaurantAnalytics.channels.callCenter",
  aggregator: "restaurantAnalytics.channels.aggregator",
};

const CUSTOM_RANGE_VALUE = "custom";

const RestaurantFilterBar: FC<RestaurantFilterBarProps> = ({ filters, setFilter, isFiltered, onReset }) => {
  const { t } = useTranslation();
  const { isRTL } = useRestaurantFormatters();

  const activePreset = useMemo(() => {
    const todayIso = isoDate(DEMO_TODAY);
    if (filters.endDate !== todayIso) {
      return CUSTOM_RANGE_VALUE;
    }
    const match = DATE_RANGES.find(id => presetStartDateFor(id) === filters.startDate);
    return match ?? CUSTOM_RANGE_VALUE;
  }, [filters.startDate, filters.endDate]);

  const applyPreset = (id: DateRangeId) => {
    setFilter("startDate", presetStartDateFor(id));
    setFilter("endDate", isoDate(DEMO_TODAY));
  };

  return (
    <div className="restaurant-filter-bar">
      <StyledDropdown
        value={activePreset}
        onChange={value => applyPreset(value as DateRangeId)}
        options={[
          ...(activePreset === CUSTOM_RANGE_VALUE
            ? [{ value: CUSTOM_RANGE_VALUE, label: t("restaurantAnalytics.filters.customRange", { defaultValue: "Custom range" }), disabled: true }]
            : []),
          ...DATE_RANGES.map(id => ({ value: id, label: t(DATE_RANGE_LABEL_KEYS[id]) })),
        ]}
        className="custom-dropdown--all-selected"
        aria-label={t("restaurantAnalytics.filters.dateRange", { defaultValue: "Date range" })}
      />

      <StyledDropdown
        value={filters.branchId}
        onChange={value => setFilter("branchId", value as string)}
        options={[
          { value: "all", label: t("restaurantAnalytics.filters.allBranches", { defaultValue: "All branches" }) },
          ...BRANCHES.map(branch => ({
            value: branch.id,
            label: isRTL ? branch.nameAr : branch.name,
          })),
        ]}
        className={filters.branchId === "all" ? "custom-dropdown--all-selected" : "custom-dropdown--has-value"}
        aria-label={t("restaurantAnalytics.filters.branch", { defaultValue: "Branch" })}
        showSearch
        optionFilterProp="label"
      />

      <StyledDropdown
        value={filters.orderType}
        onChange={value => setFilter("orderType", value as OrderTypeId | "all")}
        options={[
          { value: "all", label: t("restaurantAnalytics.filters.allOrderTypes", { defaultValue: "All order types" }) },
          ...ORDER_TYPE_MIX.map(mix => ({ value: mix.id, label: t(ORDER_TYPE_LABEL_KEYS[mix.id]) })),
        ]}
        className={filters.orderType === "all" ? "custom-dropdown--all-selected" : "custom-dropdown--has-value"}
        aria-label={t("restaurantAnalytics.filters.orderType", { defaultValue: "Order type" })}
      />

      <StyledDropdown
        value={filters.channel}
        onChange={value => setFilter("channel", value as ChannelId | "all")}
        options={[
          { value: "all", label: t("restaurantAnalytics.filters.allChannels", { defaultValue: "All channels" }) },
          ...CHANNEL_MIX.map(mix => ({ value: mix.id, label: t(CHANNEL_LABEL_KEYS[mix.id]) })),
        ]}
        className={filters.channel === "all" ? "custom-dropdown--all-selected" : "custom-dropdown--has-value"}
        aria-label={t("restaurantAnalytics.filters.channel", { defaultValue: "Channel" })}
      />

      <button
        type="button"
        className="restaurant-filter-bar__reset"
        disabled={!isFiltered}
        onClick={onReset}
        aria-label={t("restaurantAnalytics.filters.reset", { defaultValue: "Reset" })}
        title={t("restaurantAnalytics.filters.reset", { defaultValue: "Reset" })}
      >
        <RotateCcw size={14} aria-hidden />
      </button>
    </div>
  );
};

export default RestaurantFilterBar;
